import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import apiClient from "../services/api";

const AdminDonationsPage = () => {
  const navigate = useNavigate();
  const [donations, setDonations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    apiClient.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    fetchDonations();
    fetchStats();
  }, [navigate]);

  const fetchDonations = async () => {
    try {
      const response = await apiClient.get("/donations");
      setDonations(response.data);
    } catch (error) {
      console.error("Error fetching donations:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiClient.get("/donations/stats");
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="border-b bg-white">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold">Donation Management</h1>
          <Button variant="outline" onClick={() => navigate("/admin/dashboard")}>
            Back to Dashboard
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {stats && (
          <div className="grid gap-4 md:grid-cols-4 mb-8">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Total Amount</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">£{stats.total_amount.toFixed(2)}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Total Donations</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.total_donations}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Successful</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.successful}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Pending</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.pending}</div>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="space-y-4">
          <h2 className="text-xl font-bold">All Donations ({donations.length})</h2>
          {donations.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-gray-500">
                No donations yet.
              </CardContent>
            </Card>
          ) : (
            donations.map((donation) => (
              <Card key={donation.id}>
                <CardContent className="py-6">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Amount</p>
                      <p className="font-semibold text-lg">£{donation.amount.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Donor</p>
                      <p className="font-medium">
                        {donation.anonymous ? "Anonymous" : donation.donor_name || "N/A"}
                      </p>
                      {!donation.anonymous && donation.donor_email && (
                        <p className="text-xs text-gray-600">{donation.donor_email}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Status</p>
                      <p className="font-medium capitalize">{donation.status}</p>
                      {donation.gocardless_payment_id && (
                        <p className="text-xs text-gray-600">
                          ID: {donation.gocardless_payment_id.substring(0, 10)}...
                        </p>
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Date</p>
                      <p className="font-medium">
                        {new Date(donation.created_at).toLocaleDateString()}
                      </p>
                      <p className="text-xs text-gray-600">
                        {new Date(donation.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                  {donation.message && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-sm text-gray-500">Message</p>
                      <p className="text-sm mt-1">{donation.message}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDonationsPage;
